import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('lib:tools');

/**
 * Shared environment utilities for Ctrl Enter
 *
 * This module provides cross-platform environment variable handling
 * that works in both Node.js (server/main process) and browser (renderer) contexts.
 * Each application provides its own environment object for explicit control.
 */

// Only import dotenv in Node.js context (main process), not in browser context (renderer)
if (typeof window === 'undefined' && typeof process !== 'undefined') {
  // We're in Node.js (main process)
  try {
    const dotenv = require('dotenv');

    // Load environment-specific file first (e.g., .env.development)
    const nodeEnv = process.env['NODE_ENV'] || 'development';
    const envFile = `.env.${nodeEnv}`;

    // Try to load environment-specific file first
    try {
      dotenv.config({ path: envFile });
    } catch {
      // Environment-specific file doesn't exist, that's okay
    }

    // Then load .env as fallback/override
    dotenv.config();
  } catch (error) {
    // dotenv is optional - don't fail if it's not available
  }
}

/**
 * Environment object interface - each platform provides its own implementation
 */
export interface EnvironmentObject {
  [key: string]: string | undefined;
}

/**
 * Platform-agnostic helper function to get environment variables with a default value
 * @param environmentObj The environment object to read from (e.g., process.env, Vite globals, etc.)
 * @param key The environment variable key to look up
 * @param defaultValue The default value to return if the env var is not set
 * @returns The environment variable value or the default value
 */
export const getEnvVar = (
  environmentObj: EnvironmentObject,
  key: string,
  defaultValue: string
): string => {
  const value = environmentObj[key];
  if (!value) {
    logger.warn(`[ENV]: missing ${key}, using default: ${defaultValue}`);
  }
  return value || defaultValue;
};

/**
 * Helper function to get required environment variables
 * @param environmentObj The environment object to read from
 * @param key The environment variable key to look up
 * @throws Error if the environment variable is not set
 * @returns The environment variable value
 */
export const getRequiredEnvVar = (environmentObj: EnvironmentObject, key: string): string => {
  const value = getEnvVar(environmentObj, key, '');
  if (!value) {
    throw new Error(`Required environment variable ${key} is not set`);
  }
  return value;
};

/**
 * Helper function to get boolean environment variables
 * @param environmentObj The environment object to read from
 * @param key The environment variable key to look up
 * @param defaultValue The default boolean value
 * @returns The parsed boolean value
 */
export const getBooleanEnvVar = (
  environmentObj: EnvironmentObject,
  key: string,
  defaultValue: boolean
): boolean => {
  const value = getEnvVar(environmentObj, key, '');
  if (!value) {
    return defaultValue;
  }
  return value.toLowerCase() === 'true';
};

/**
 * Helper function to get numeric environment variables
 * @param environmentObj The environment object to read from
 * @param key The environment variable key to look up
 * @param defaultValue The default numeric value
 * @returns The parsed numeric value
 */
export const getNumericEnvVar = (
  environmentObj: EnvironmentObject,
  key: string,
  defaultValue: number
): number => {
  const value = getEnvVar(environmentObj, key, '');
  if (!value) {
    return defaultValue;
  }
  const parsed = parseInt(value, 10);
  if (isNaN(parsed)) {
    logger.warn(`[ENV]: ${key} is not a valid number, using default: ${defaultValue}`);
    return defaultValue;
  }
  return parsed;
};

/**
 * Environment detection utilities
 */
export const getNodeEnv = (environmentObj: EnvironmentObject): string =>
  getEnvVar(environmentObj, 'NODE_ENV', 'development');
export const isProduction = (environmentObj: EnvironmentObject): boolean =>
  getNodeEnv(environmentObj) === 'production';
export const isDevelopment = (environmentObj: EnvironmentObject): boolean =>
  getNodeEnv(environmentObj) === 'development';
export const isTest = (environmentObj: EnvironmentObject): boolean =>
  getNodeEnv(environmentObj) === 'test';

/**
 * Common environment variables used across the application
 * @param environmentObj The environment object to read from
 */
export const createEnvConfig = (environmentObj: EnvironmentObject) => ({
  // Core configuration
  NODE_ENV: getNodeEnv(environmentObj),
  API_PUBLIC_KEY:
    getEnvVar(environmentObj, 'VITE_API_PUBLIC_KEY', '') ||
    getEnvVar(environmentObj, 'API_PUBLIC_KEY', ''),
  isProduction: isProduction(environmentObj),
  isDevelopment: isDevelopment(environmentObj),
  isTest: isTest(environmentObj),

  // API Keys
  OPENAI_API_KEY: getEnvVar(environmentObj, 'OPENAI_API_KEY', ''),

  // WorkOS Authentication
  WORKOS_CLIENT_ID: getEnvVar(environmentObj, 'WORKOS_CLIENT_ID', ''),
  WORKOS_API_KEY: getEnvVar(environmentObj, 'WORKOS_API_KEY', ''),
  WORKOS_EXPECTED_ISS: getEnvVar(environmentObj, 'WORKOS_EXPECTED_ISS', 'https://api.workos.com/'),
  WORKOS_REDIRECT_URI: getEnvVar(environmentObj, 'WORKOS_REDIRECT_URI', ''),
  // Stripe Configuration
  STRIPE_PUBLISHABLE_KEY: getEnvVar(environmentObj, 'VITE_STRIPE_PUBLISHABLE_KEY', ''),
  STRIPE_PRICING_TABLE_ID: getEnvVar(environmentObj, 'VITE_STRIPE_PRICING_TABLE_ID', ''),
  STRIPE_SUBSCRIPTION_PORTAL_URL: getEnvVar(
    environmentObj,
    'VITE_STRIPE_SUBSCRIPTION_PORTAL_URL',
    ''
  ),
  STRIPE_SECRET_KEY: getEnvVar(environmentObj, 'STRIPE_SECRET_KEY', ''),
  STRIPE_WEBHOOK_SECRET: getEnvVar(environmentObj, 'STRIPE_WEBHOOK_SECRET', ''),

  // Entitlement Configuration
  SUBSCRIPTION_ENTITLEMENT_NAME: getEnvVar(
    environmentObj,
    'SUBSCRIPTION_ENTITLEMENT_NAME',
    'ctrl-enter'
  ),

  // API Configuration
  API_BASE_URL: getEnvVar(
    environmentObj,
    'VITE_API_BASE_URL',
    'http://service-gateway-1llw.onrender.com'
  ),

  // Server Configuration (Node.js only)
  PORT: getNumericEnvVar(environmentObj, 'PORT', 10000),
  HOST: getEnvVar(environmentObj, 'HOST', 'service-gateway-1llw.onrender.com'),

  // CORS Configuration
  ALLOWED_ORIGINS: getEnvVar(environmentObj, 'ALLOWED_ORIGINS', '*'),

  // Telemetry Configuration
  ENABLE_TELEMETRY: getBooleanEnvVar(environmentObj, 'ENABLE_TELEMETRY', false),
  OTEL_EXPORTER_OTLP_ENDPOINT: getEnvVar(
    environmentObj,
    'OTEL_EXPORTER_OTLP_ENDPOINT',
    'http://localhost:4318'
  ),
  OTEL_EXPORTER_OTLP_HEADERS: getEnvVar(environmentObj, 'OTEL_EXPORTER_OTLP_HEADERS', ''),

  // AWS Configuration (for development/testing only - do not use in production)
  AWS_REGION: getEnvVar(environmentObj, 'AWS_REGION', 'us-east-1'),
  AWS_ACCESS_KEY_ID: getEnvVar(environmentObj, 'AWS_ACCESS_KEY_ID', ''),
  AWS_SECRET_ACCESS_KEY: getEnvVar(environmentObj, 'AWS_SECRET_ACCESS_KEY', ''),
});

export type EnvConfig = ReturnType<typeof createEnvConfig>;
